import { useAuth0 } from '@auth0/auth0-react';

import {
  FetchAutocomplete,
  FetchAutocompleteChangeReason,
  domainCodeAtom,
} from '@australiangreens/ag-internal-components';
import { AccordionField } from '@components/DrawerAccordion';
import { Search } from '@mui/icons-material';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { getTagsContaining } from '@/apis/tags';
import { labelSorting } from '@/util/labelSorting';

// The TagRecord[] returned from getTagsContaining has name, but we want
// label
export interface TagRecordResult {
  id: number;
  label: string;
}

type TagsFetchAutocompleteProps = {
  onChange: (
    newValue: TagRecordResult[],
    reason: FetchAutocompleteChangeReason,
    event: React.SyntheticEvent<Element, Event>
  ) => void;
  value: TagRecordResult[];
  minLength?: number;
  tagLabel?: string;
  dataTestidPrefix?: string;
  loadingText?: string;
  noOptionsText?: string;
  error?: boolean;
  helperText?: React.ReactNode;
};

export const useLookupTags = () => {
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);

  return useCallback(
    async (lookupValue: string) => {
      const token = await getAccessTokenSilently();
      return (await getTagsContaining(token, lookupValue, domainCode)).sort(labelSorting);
    },
    [domainCode, getAccessTokenSilently]
  );
};

const TagsFetchAutocomplete = ({
  value = [] as TagRecordResult[],
  onChange,
  minLength = 3,
  tagLabel = 'Find a tag',
  dataTestidPrefix = 'Find a tag',
  loadingText = 'Looking for tags...',
  noOptionsText = 'No tags found',
  error = false,
  helperText = '',
}: TagsFetchAutocompleteProps) => {
  const lookupTags = useLookupTags();

  return (
    <AccordionField>
      <FetchAutocomplete
        lookup={lookupTags}
        minLength={minLength}
        label={tagLabel}
        value={value}
        onChange={onChange}
        data-testid={dataTestidPrefix}
        loadingText={loadingText}
        noOptionsText={noOptionsText}
        popupIcon={<Search />}
        disableIconFlip
        error={error}
        helperText={helperText}
      />
    </AccordionField>
  );
};

export default TagsFetchAutocomplete;
